import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { scopeToSite } from '../../lib/siteScope';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { IconDownload } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/LanguageContext';

export default function FuelUsageReport() {
  const { language } = useTranslation();
  const { orgId, userProfile, role } = useAuth();
  const siteId = userProfile?.siteId;
  const [tanks, setTanks] = useState([]);
  const [fills, setFills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const tanksSnap = await getDocs(collection(db, 'organisations', orgId, 'dervTanks'));
        const tankList = scopeToSite(tanksSnap.docs.map(d => ({ id: d.id, ...d.data() })), role, siteId);
        setTanks(tankList);
        const allFills = [];
        await Promise.all(tankList.map(async (tank) => {
          const snap = await getDocs(query(
            collection(db, 'organisations', orgId, 'dervTanks', tank.id, 'fills'),
            orderBy('timestamp', 'desc')
          ));
          snap.docs.forEach(d => allFills.push({ id: d.id, tankId: tank.id, tankName: tank.name, ...d.data() }));
        }));
        setFills(allFills);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const totalLitres = fills.reduce((sum, f) => sum + (f.litresAdded || 0), 0);
  const totalCost = fills.reduce((sum, f) => sum + (f.cost || 0), 0);
  const now = new Date();
  const thisMonth = fills.filter(f => {
    const d = f.timestamp?.toDate ? f.timestamp.toDate() : new Date(f.timestamp);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const thisMonthLitres = thisMonth.reduce((sum, f) => sum + (f.litresAdded || 0), 0);

  const byMachine = fills.reduce((acc, f) => {
    if (!f.machineId) return acc;
    if (!acc[f.machineId]) acc[f.machineId] = { name: f.machineName || 'Unknown', litres: 0, cost: 0 };
    acc[f.machineId].litres += f.litresAdded || 0;
    acc[f.machineId].cost += f.cost || 0;
    return acc;
  }, {});

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="Fuel usage report"
          subtitle="Derv consumption across all tanks"
          actions={<Button variant="secondary" icon={<IconDownload size={14} stroke={1.5} />} onClick={() => toast.info('PDF export coming soon')}>Export PDF</Button>}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total fills', value: fills.length, colour: 'var(--color-text-primary)' },
            { label: 'Total litres', value: totalLitres.toLocaleString() + 'L', colour: 'var(--color-text-primary)' },
            { label: 'This month', value: thisMonthLitres.toLocaleString() + 'L', colour: 'var(--color-brand)' },
            { label: 'Total cost', value: totalCost ? '£' + totalCost.toLocaleString() : '—', colour: 'var(--color-text-primary)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 24, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>
        {Object.keys(byMachine).length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Usage by machine</div>
            {Object.entries(byMachine).sort((a, b) => b[1].litres - a[1].litres).map(([id, data], i, arr) => (
              <div key={id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < arr.length - 1 ? '0.5px solid var(--color-border-light)' : 'none' }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{data.name}</div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{data.litres.toLocaleString()}L</div>
                  {data.cost > 0 && <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>£{data.cost.toLocaleString()}</div>}
                </div>
              </div>
            ))}
          </Card>
        )}
        <Card padding="0">
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {['Tank', 'Date', 'Litres added', 'Cost'].map(h => (
              <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>
          {loading ? <LoadingSkeleton rows={6} /> : fills.slice(0, 50).map((fill, i) => {
            const d = fill.timestamp?.toDate ? fill.timestamp.toDate() : fill.timestamp ? new Date(fill.timestamp) : null;
            return (
              <div key={fill.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', padding: '10px 16px', borderBottom: i < fills.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', alignItems: 'center' }}>
                <div style={{ fontSize: 12, color: 'var(--color-text-primary)' }}>{fill.tankName}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{d ? d.toLocaleDateString(language) : '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--color-success)' }}>+{fill.litresAdded?.toLocaleString()}L</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{fill.cost ? '£' + fill.cost.toLocaleString() : '—'}</div>
              </div>
            );
          })}
        </Card>
      </div>
    </Layout>
  );
}